import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Grid } from "@mui/material";
import PageHeader from "components/Header/Page";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { fetchSectorList, workspaceGetEnv } from "service/service";
import { CustomizedDataGrid } from "components/customized_data_grid";
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import LoaderComponent from 'components/Loader';
import FiltersBar from './FilterBar';
import { extractDatesFromNode, findSelectedNode, currencyFormatter } from 'utils/common-util';
import {
    commonFetchSectors,
    useAdbFilters,
    formatCompactNumber,
    useAdbHierarchyTable,
    syncEnvWithOptions,
    resolveTreeNodeCount
} from 'utils/adb-common-util';
import PageWrapper from 'components/Common/PageWraper';

const getColumns = (
    columnLabel: string,
    isCurrency: boolean,
    totalValue: number,
    toggleExpandRow: (id: string) => void,
    handleRowClick: (row: any) => void
) => [
        {
            key: 'time_range', name: 'Time Range', minWidth: 200, formatter: ({ row, isCellSelected }: any) => (
                <Box sx={{ display: 'flex', alignItems: 'center', pl: row.level * 2 }}>
                    {row.children && row.children.length > 0 && (
                        <CellExpanderFormatter
                            isCellSelected={isCellSelected}
                            expanded={row.isExpanded}
                            onCellExpand={() => toggleExpandRow(row.id)}
                        />
                    )}
                    <span>{row.time_range}</span>
                </Box>
            )
        },
        {
            key: 'value', name: columnLabel, minWidth: 180, formatter: ({ row }: any) => (
                <Box sx={{ cursor: 'pointer', color: '#4fc3f7', textAlign: 'left' }} onClick={() => handleRowClick(row)}>
                    {isCurrency ? currencyFormatter.format(row.count ?? 0) : formatCompactNumber(row.count ?? 0)}
                </Box>
            )
        },
        {
            key: 'percent_of_total', name: '% of Total', minWidth: 150, formatter: ({ row }: any) => (
                <Box sx={{ textAlign: 'left' }}>
                    {totalValue > 0 ? ((row.count / totalValue) * 100).toFixed(2) : '0.00'}%
                </Box>
            )
        }
    ];

export interface FetchServiceParams {
    startDate: string;
    endDate: string;
    env: string;
    sector: string;
    location: string;
    computeType: string;
    page: number;
    pageSize: number;
    filters: any[];
    apiDateRangeKey: string;
}

interface ComputeHierarchyBaseProps {
    serviceFunc: (params: FetchServiceParams) => Promise<any>;
    pageTitle: string;
    tableTitlePrefix: string;
    columnLabel: string;
    isCurrency: boolean;
    preserveFilterKey: string;
    detailPath: string;
    tableType: string;
    breadcrumbLabel?: string;
    breadcrumbPath?: string;
    additionalNavState?: any;
}

const ComputeHierarchyBase: React.FC<ComputeHierarchyBaseProps> = ({
    serviceFunc,
    pageTitle,
    tableTitlePrefix,
    columnLabel,
    isCurrency,
    preserveFilterKey,
    detailPath,
    tableType,
    breadcrumbLabel = 'Workspace Overview',
    breadcrumbPath = '/databricksADB/workspace',
    additionalNavState = {}
}) => {
    const navigate = useNavigate();
    const {
        startDate, setStartDate, endDate, setEndDate,
        env, setEnv, sector, setSector, sectorName, setSectorName,
        location, setLocation, selectedDateInString, setSelectedDateInString,
        apiDateRangeKey, updateHistoryState, handleDateSelect, restoredRef, envRef, initialEnvAppliedRef, navigationState
    } = useAdbFilters(preserveFilterKey);

    const { computeType = '' } = navigationState || {};
    const [loader, setLoader] = useState(false);
    const [apiData, setApiData] = useState<any[]>([]);
    const [totalValue, setTotalValue] = useState<number>(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [envOptions, setEnvOptions] = useState<{ label: string; value: string }[]>([]);
    const [sectorList, setSectorList] = useState<string[]>([]);

    const { tableData, toggleExpandRow, paginationData, rowsRef } = useAdbHierarchyTable(apiData, pageSize);

    useEffect(() => { commonFetchSectors(fetchSectorList, setSectorList); }, []);

    useEffect(() => {
        const getEnvFetch = async (loc: string) => {
            try {
                const res = await workspaceGetEnv(loc);
                const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
                setEnvOptions(options);
                syncEnvWithOptions(options, initialEnvAppliedRef, envRef, navigationState?.env, setEnv);
            } catch (err) { console.error(err); }
        };
        getEnvFetch(location);
    }, [location, navigationState?.env]);

    useEffect(() => {
        const fetchData = async () => {
            setLoader(true);
            try {
                const filters = computeType && computeType.trim() ? [{ operator: 'AND', field: 'computetype', value: computeType }] : [];
                const resp = await serviceFunc({
                    startDate,
                    endDate,
                    env,
                    sector,
                    location,
                    computeType: computeType || '',
                    page,
                    pageSize,
                    filters,
                    apiDateRangeKey
                });
                if (resp?.success && Array.isArray(resp.data)) {
                    setApiData(resp.data);
                    let total = 0;
                    if (resp.data.length > 0) {
                        const firstItem = resp.data[0];
                        if (firstItem.year) {
                            total = resolveTreeNodeCount(firstItem.year);
                        } else {
                            total = resp.data.reduce((sum: number, item: any) => sum + resolveTreeNodeCount(item[Object.keys(item)[0]]), 0);
                        }
                    }
                    setTotalValue(total);
                } else { setApiData([]); setTotalValue(0); }
            } catch (error) { console.error(error); setApiData([]); setTotalValue(0); }
            finally {
                setLoader(false);
                if (restoredRef.current) { sessionStorage.removeItem(preserveFilterKey); restoredRef.current = false; }
            }
        };
        fetchData();
    }, [startDate, endDate, env, sector, location, selectedDateInString, apiDateRangeKey, page, pageSize, computeType, serviceFunc, preserveFilterKey]);

    const handleRowClick = useCallback((row: any) => {
        let type: 'year' | 'month' | 'week' | 'day' = 'year';
        if (row.id.includes('day')) type = 'day';
        else if (row.id.includes('week')) type = 'week';
        else if (row.id.includes('month')) type = 'month';

        const found = findSelectedNode(apiData, type, row.time_range, row.id);
        const navStateBase: any = {
            dateLabel: row.time_range,
            startDate: row.date || startDate,
            endDate: row.date || endDate,
            env, sector, location, selectedDateInString, computeType,
            parentPageTitle: pageTitle,
            ...additionalNavState
        };

        if (found?.kind) {
            const dates = extractDatesFromNode(found.node);
            if (found.kind === 'day' && found.node?.date) {
                const d = moment(found.node.date, 'YYYY-MM-DD').format('YYYY-MM-DD');
                navStateBase.startDate = d; navStateBase.endDate = d; navStateBase.selectedDateInString = 'customDateRange';
            } else if (dates.length > 0) {
                const moments = dates.map((ds: string) => moment(ds, 'YYYY-MM-DD'));
                navStateBase.startDate = moment.min(moments).format('YYYY-MM-DD');
                navStateBase.endDate = moment.max(moments).format('YYYY-MM-DD');
                navStateBase.selectedDateInString = 'customDateRange';
            }
            setPage(1); rowsRef.current = [];
        }
        sessionStorage.setItem(preserveFilterKey, JSON.stringify({ startDate, endDate, selectedDateInString }));
        navigate(detailPath, { state: navStateBase });
    }, [navigate, apiData, startDate, endDate, env, sector, location, selectedDateInString, computeType, rowsRef, detailPath, preserveFilterKey, pageTitle, additionalNavState]);

    const renderTableContent = () => {
        if (loader) {
            return <LoaderComponent pt={20} />;
        }
        if (tableData.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 10, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ fontSize: '1.2rem', color: '#666', mb: 1 }}>No Data Available</Box>
                    <Box sx={{ fontSize: '0.9rem', color: '#999' }}>No data found for the selected filters.</Box>
                </Box>
            );
        }
        return (
            <CustomizedDataGrid
                title={`${tableTitlePrefix} (${isCurrency ? currencyFormatter.format(totalValue) : formatCompactNumber(totalValue)})`}
                tableColumns={columns} tableData={tableData} tableType={tableType}
                defaultColumnNames={['Time Range', columnLabel, '% of Total']}
                page={page} pageSize={pageSize} paginationData={paginationData} listLength={tableData.length}
                handlepageSizeChange={(e: any) => { setPageSize(Number(e.target.value)); setPage(1); }}
                handlePageChange={(_: any, v: number) => setPage(v)}
                isDateRangeFilter={false} isDisabled={false} isFetching={loader}
            />
        );
    };

    const columns = useMemo(() => getColumns(columnLabel, isCurrency, totalValue, toggleExpandRow, handleRowClick), [toggleExpandRow, handleRowClick, totalValue, columnLabel, isCurrency]);

    return (
        <PageWrapper>
            <PageHeader
                title={[
                    {
                        text: breadcrumbLabel,
                        link: breadcrumbPath,
                        state: { startDate, endDate, env, sector, location, selectedDateInString }
                    },
                    {
                        text: `${pageTitle}${computeType ? ` (${computeType})` : ''}`
                    }
                ]}
                isPepwise2Breadcrumb={true}
            />

            <FiltersBar
                location={location} setLocation={setLocation}
                env={env} setEnv={setEnv} envOptions={envOptions} setEnvOptions={setEnvOptions}
                startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate}
                handleDateSelect={handleDateSelect} selectedDateInString={selectedDateInString} setSelectedDateInString={setSelectedDateInString}
                sectorName={sectorName}
                setSectorName={(v: any) => {
                    const resolved = typeof v === 'function' ? v(sectorName) : v;
                    setSectorName(resolved);
                    setSector(resolved);
                    setEnv('both');
                }}
                updateHistoryState={updateHistoryState}
                sectorOptionsList={sectorList}
                useHeaderFilters
            />

            <Grid container spacing={2} px={2}>
                <Grid item xs={12}>
                    {renderTableContent()}
                </Grid>
            </Grid>
        </PageWrapper>
    );
};

export default ComputeHierarchyBase;
